import { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Collapse,
  IconButton,
  Divider,
  Chip,
} from "@mui/material";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ApproveSolistapi,
  ProjectSummaryStats,
  ProjectSummaryRpt,
} from "../../../Services/Services";
import { Card, CardContent, Grid } from "@mui/material";
import { useMediaQuery, useTheme } from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import ProSummaryPi from "../Reports/ProjectSummary/ProSummaryPi";
import Solist from "../Reports/ProjectSummary/Solist";
import GeneralVoucher from "../Reports/ProjectSummary/GeneralVoucher";
import PurchaseVoucher from "../Reports/ProjectSummary/PurchaseVoucher";
import ReceiptVoucher from "../Reports/ProjectSummary/ReceiptVoucher";
import SaleVoucher from "../Reports/ProjectSummary/SalesVoucher";
import PaymentVoucher from "../Reports/ProjectSummary/PaymentVoucher";
import Material_inward_register from "../Reports/ProjectSummary/IssueNoteInward";
import Material_issue_register from "../Reports/ProjectSummary/IssueNoteOutward";
import Expense from "./ProjectSummary/Expense";
import debounce from "lodash.debounce";
import { ToastContainer } from "react-toastify";

const CARD_COLORS = {
  SO: {
    bg: "linear-gradient(135deg,#F1F8E9 0%,#DCEDC8 100%)",
    border: "#7CB342",
    text: "#33691E",
  },
  PI: {
    bg: "linear-gradient(135deg,#E3F2FD 0%,#BBDEFB 100%)",
    border: "#2196f3",
    text: "#0D47A1",
  },
  Journal: {
    bg: "linear-gradient(135deg,#F3E5F5 0%,#E1BEE7 100%)",
    border: "#9C27B0",
    text: "#4A148C",
  },
  PurchaseVoucher: {
    bg: "linear-gradient(135deg,#FFF3E0 0%,#FFE0B2 100%)",
    border: "#FB8C00",
    text: "#E65100",
  },
  SalesVoucher: {
    bg: "linear-gradient(135deg,#EDE7F6 0%,#C5CAE9 100%)",
    border: "#3F51B5",
    text: "#1A237E",
  },
  Payment: {
    bg: "linear-gradient(135deg,#FFEBEE 0%,#FFCDD2 100%)",
    border: "#E53935",
    text: "#B71C1C",
  },
  Receipt: {
    bg: "linear-gradient(135deg,#E8F5E9 0%,#C8E6C9 100%)",
    border: "#43A047",
    text: "#1B5E20",
  },
  Indent: {
    bg: "linear-gradient(135deg,#ECEFF1 0%,#CFD8DC 100%)",
    border: "#607D8B",
    text: "#263238",
  },
  PO: {
    bg: "linear-gradient(135deg,#FFF8E1 0%,#FFECB3 100%)",
    border: "#FFA000",
    text: "#FF6F00",
  },
  GoodOut: {
    bg: "linear-gradient(135deg,#FFF3E0 0%,#FFE0B2 100%)",
    border: "#F57C00",
    text: "#E65100",
  },
  GoodIn: {
    bg: "linear-gradient(135deg,#F1F8E9 0%,#DCEDC8 100%)",
    border: "#7CB342",
    text: "#33691E",
  },
  Transfer: {
    bg: "linear-gradient(135deg,#E0F7FA 0%,#B2EBF2 100%)",
    border: "#00ACC1",
    text: "#006064",
  },
  IssueNoteInward: {
    bg: "linear-gradient(135deg,#E0F7FA 0%,#B2EBF2 100%)",
    border: "#00ACC1",
    text: "#006064",
  },
  IssueNoteOutward: {
    bg: "linear-gradient(135deg,#E0F7FA 0%,#B2EBF2 100%)",
    border: "#00ACC1",
    text: "#006064",
  },
  Expense: {
    bg: "linear-gradient(135deg,#E0F7FC 0%,#B2EBF2 100%)",
    border: "#00ACC1",
    text: "#006064",
  },
};

const ProjectSummary = () => {
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectAmt, setprojectAmt] = useState({});
  const [tabData, setTabData] = useState([]);
  const [selectedTab, setSelectedTab] = useState();
  const [expandedRow, setExpandedRow] = useState(null);
  const [projectSearch, setProjectSearch] = useState("");
  const [projectPage, setProjectPage] = useState(1);
  const [projectHasMore, setProjectHasMore] = useState(true);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // ✅ Removed division dependency – projects load directly

  useEffect(() => {
    document.body.style.overflow = isMobile ? "auto" : "hidden";

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMobile]);

  const fetchReportData = async (key) => {
    if (!selectedProject) return;
    try {
      const res = await ProjectSummaryRpt(selectedProject.id, { key: key });
      setTabData(res || []);
      setExpandedRow(null);
    } catch (error) {
      console.error("Failed to fetch report:", error);
      setTabData([]);
    }
  };

  // ✅ Modified: removed division filter from API call
  const fetchProjects = async (page = 1, search = "") => {
    setLoadingProjects(true);
    try {
      const res = await ApproveSolistapi(page, {
        search,
        // No division filter
      });

      if (page === 1) {
        setProjects(res?.results || res || []);
      } else {
        setProjects((prev) => [...prev, ...(res?.results || res || [])]);
      }

      setProjectHasMore(!!res?.next);
    } catch (e) {
      console.error("Projects fetch failed", e);
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  };

  // ✅ Debounced search handler
  const handleProjectSearch = debounce((value) => {
    setProjectSearch(value);
    setProjectPage(1);
    fetchProjects(1, value);
  }, 500);

  // ✅ Load initial projects on mount (no division check)
  useEffect(() => {
    fetchProjects(1, "");
  }, []);

  useEffect(() => {
    const loadSummary = async () => {
      if (!selectedProject) return;
      try {
        const res = await ProjectSummaryStats(selectedProject.id);
        setprojectAmt(res?.summary || {});
      } catch (e) {
        setprojectAmt({});
      }
    };
    loadSummary();
  }, [selectedProject]);

  const renderSection = () => {
    if (!selectedProject) return null;
    switch (selectedTab) {
      case "SO":
        return <Solist data={tabData} />;
      case "PI":
        return <ProSummaryPi data={tabData} />;
      case "Journal":
        return <GeneralVoucher data={tabData} />;
      case "PurchaseVoucher":
        return <PurchaseVoucher data={tabData} />;
      case "SalesVoucher":
        return <SaleVoucher data={tabData} />;
      case "Payment":
        return <PaymentVoucher data={tabData} />;
      case "Receipt":
        return <ReceiptVoucher data={tabData} />;
      case "IssueNoteInward":
        return <Material_inward_register data={tabData} />;
      case "IssueNoteOutward":
        return <Material_issue_register data={tabData} />;
      case "Expense":
        return <Expense data={tabData} />;
      default:
        return null;
    }
  };

  // ✅ Mobile Data Renderer - Cash Ledger Style (unchanged)
  const renderMobileData = () => {
    if (!selectedTab || !tabData) return null;

    const dataArray = Array.isArray(tabData)
      ? tabData
      : tabData?.results || tabData?.data || [];

    if (dataArray.length === 0) {
      return (
        <Box
          sx={{
            p: 3,
            mt: 10,
            textAlign: "center",
            bgcolor: "#f5f5f5",
            borderRadius: 1,
          }}
        >
          <Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
            No data available
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ bgcolor: "background.paper" }}>
        {dataArray.map((row, index) => {
          let identifier = "";
          let partyName = "";
          let amount = 0;

          if (selectedTab === "Expense") {
            identifier =
              row.expense_number || row.voucher_no || `EXP-${index + 1}`;
            partyName =
              row.party_name || row.supplier_name || row.category || "N/A";
            amount = row.total_amount || row.amount || row.total || 0;
          } else {
            identifier =
              row.so_no ||
              row.pi_no ||
              row.voucher_no ||
              row.number ||
              row.gen_number ||
              `#${index + 1}`;
            partyName =
              row.party_name ||
              row.party_details?.company_name ||
              row.supplier_name ||
              row.customer_name ||
              row.name ||
              "N/A";
            amount =
              row.grand_total ||
              row.total_amount ||
              row.amount ||
              row.total ||
              0;
          }

          return (
            <Box key={index}>
              <Box
                onClick={() =>
                  setExpandedRow(expandedRow === index ? null : index)
                }
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 1.5,
                  cursor: "pointer",
                  bgcolor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                  "&:active": { bgcolor: "#e3f2fd" },
                  borderLeft: `4px solid ${CARD_COLORS[selectedTab]?.border || "#1976d2"}`,
                  mt: {xs: 4,sm: 0},
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      sx={{ fontSize: "0.75rem", fontWeight: "bold" }}
                    >
                      {identifier}
                    </Typography>
                    {row.status && (
                      <Chip
                        label={row.status}
                        color={
                          row.status === "Approved"
                            ? "success"
                            : row.status === "Rejected"
                              ? "error"
                              : "warning"
                        }
                        size="small"
                        sx={{ fontSize: "0.6rem", height: 18 }}
                      />
                    )}
                  </Box>

                  <Typography
                    sx={{ fontSize: "0.7rem", color: "#666", mb: 0.3 }}
                  >
                    {partyName}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: "0.8rem",
                      fontWeight: "bold",
                      color: "primary.main",
                    }}
                  >
                    ₹{Number(amount).toLocaleString("en-IN")}
                  </Typography>
                </Box>

                <IconButton size="small" sx={{ ml: 1 }}>
                  {expandedRow === index ? (
                    <ExpandLess fontSize="small" />
                  ) : (
                    <ExpandMore fontSize="small" />
                  )}
                </IconButton>
              </Box>

              <Collapse in={expandedRow === index} timeout="auto" unmountOnExit>
                <Box
                  sx={{
                    px: 1.5,
                    py: 1,
                    bgcolor: index % 2 === 0 ? "#f8f9fa" : "#fafafa",
                    borderLeft: `4px solid ${CARD_COLORS[selectedTab]?.border || "#1976d2"}`,
                  }}
                >
                  {Object.entries(row).map(([key, value]) => {
                    const skipFields = [
                      "id",
                      "_id",
                      "so_no",
                      "pi_no",
                      "voucher_no",
                      "number",
                      "gen_number",
                      "expense_number",
                      "status",
                      "party_name",
                      "party_details",
                      "grand_total",
                      "total_amount",
                      "amount",
                      "total",
                      "supplier_name",
                      "customer_name",
                      "name",
                    ];
                    if (skipFields.includes(key)) return null;
                    if (typeof value === "object" && value !== null)
                      return null;

                    return (
                      <Box key={key} sx={{ mb: 0.8 }}>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "#999",
                            letterSpacing: "0.5px",
                          }}
                        >
                          {key.replace(/_/g, " ").toUpperCase()}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.72rem",
                            fontWeight: 500,
                            color: "#333",
                          }}
                        >
                          {value || "N/A"}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Collapse>

              <Divider />
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      <ToastContainer position="top-right" autoClose={3000} />
      <Box sx={{ display: "flex", gap: 2, mb: 2, mt: {xs:3.5,sm:0} }}>
        <Autocomplete
          options={Array.isArray(projects) ? projects : []}
          getOptionLabel={(option) =>
            `${option.project_name || ""} | ${option.po_number || ""} | ${option.party_details?.company_name || option.party_name || ""}`
          }
          loading={loadingProjects}
          value={selectedProject}
          onChange={(e, v) => setSelectedProject(v)}
          // ✅ Removed disabled prop – always enabled
          onInputChange={(e, value, reason) => {
            if (reason === "input") {
              handleProjectSearch(value);
            }
          }}
          ListboxProps={{
            onScroll: (event) => {
              const listboxNode = event.currentTarget;
              if (
                listboxNode.scrollTop + listboxNode.clientHeight ===
                listboxNode.scrollHeight
              ) {
                if (projectHasMore && !loadingProjects) {
                  setProjectPage((prev) => prev + 1);
                  fetchProjects(projectPage + 1, projectSearch);
                }
              }
            },
          }}
          noOptionsText="No projects found"
          sx={{ flex: 1 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Project"
              placeholder="Search project..."
              size="small"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingProjects ? (
                      <CircularProgress color="inherit" size={18} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              sx={{
                "& .MuiInputBase-root": { height: 36, borderRadius: 0 },
                "& .MuiInputLabel-root": { fontSize: "0.875rem" },
              }}
            />
          )}
        />
      </Box>

      {/* Summary cards (unchanged) */}
      {selectedProject && (
        <Box sx={{ mb: 2, minHeight: "200px" }}>
          <Grid container spacing={2}>
            {/* 1. Sales Order (SO) Card */}
            <Grid item xs={6} sm={6} md={4} lg={2.4}>
              <Card
                onClick={() => {
                  setSelectedTab("SO");
                  fetchReportData("SO");
                }}
                sx={{
                  width: "100%",
                  minHeight: { xs: 80, sm: 50 },
                  cursor: "pointer",
                  p: { xs: 0.3, sm: 0.5 },
                  background: CARD_COLORS.SO.bg,
                  border:
                    selectedTab === "SO"
                      ? "2px solid #1976d2"
                      : `1px solid ${CARD_COLORS.SO.border}`,
                  color: CARD_COLORS.SO.text,
                  borderRadius: 1.5,
                  boxShadow: 1,
                  transition: "transform .18s ease, box-shadow .18s ease",
                  "&:hover": { transform: "translateY(-3px)", boxShadow: 3 },
                }}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: { xs: 0.3, sm: 0.5 },
                    p: { xs: "8px !important", sm: "12px !important" },
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: "0.7rem", sm: "14px" },
                      color: CARD_COLORS.SO.text,
                      textAlign: "center",
                    }}
                  >
                    Sales Order
                  </Typography>
                  {/* <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 400,
                      fontSize: { xs: "0.65rem", sm: "14px" },
                      color: CARD_COLORS.SO.text,
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <span>Total:</span>
                    <span>
                      ₹
                      {projectAmt.sale_order
                        ? projectAmt.sale_order?.total_amount
                        : 0}
                    </span>
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 400,
                      fontSize: { xs: "0.6rem", sm: "13px" },
                      color: CARD_COLORS.SO.text,
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <span>Rec:</span>
                    <span>
                      ₹
                      {projectAmt.sale_order
                        ? projectAmt.sale_order?.receive_amt
                        : 0}
                    </span>
                  </Typography> */}
                </CardContent>
              </Card>
            </Grid>

            {/* 2. Proforma Invoice (PI) Card */}
            <Grid item xs={6} sm={6} md={4} lg={2.4}>
              <Card
                onClick={() => {
                  setSelectedTab("PI");
                  fetchReportData("PI");
                }}
                sx={{
                  width: "100%",
                  minHeight: { xs: 80, sm: 50 },
                  cursor: "pointer",
                  p: { xs: 0.3, sm: 0.5 },
                  background: CARD_COLORS.PI.bg,
                  border:
                    selectedTab === "PI"
                      ? "2px solid #1976d2"
                      : `1px solid ${CARD_COLORS.PI.border}`,
                  color: CARD_COLORS.PI.text,
                  borderRadius: 1.5,
                  boxShadow: 1,
                  transition: "transform .18s ease, box-shadow .18s ease",
                  "&:hover": { transform: "translateY(-3px)", boxShadow: 3 },
                }}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: { xs: 0.3, sm: 0.5 },
                    p: { xs: "8px !important", sm: "12px !important" },
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: "0.7rem", sm: "14px" },
                      color: CARD_COLORS.PI.text,
                      textAlign: "center",
                    }}
                  >
                    Proforma Invoice
                  </Typography>
                  {/* <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 400,
                      fontSize: { xs: "0.65rem", sm: "14px" },
                      color: CARD_COLORS.PI.text,
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <span>Total:</span>
                    <span>
                      ₹
                      {projectAmt.project_pi
                        ? (projectAmt.project_pi?.total_amount).toFixed(2)
                        : 0}
                    </span>
                  </Typography> */}
                  {/* <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 400,
                      fontSize: { xs: "0.6rem", sm: "13px" },
                      color: CARD_COLORS.PI.text,
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <span>Rec:</span>
                    <span>
                      ₹
                      {projectAmt.project_pi
                        ? projectAmt.project_pi?.receive_amt
                        : 0}
                    </span>
                  </Typography> */}
                </CardContent>
              </Card>
            </Grid>

            {/* 3. Journal Voucher Card */}
            {/* <Grid item xs={6} sm={6} md={4} lg={2.4}>
              <Card
                onClick={() => {
                  setSelectedTab("Journal");
                  fetchReportData("Journal");
                }}
                sx={{
                  width: "100%",
                  minHeight: { xs: 80, sm: 50 },
                  cursor: "pointer",
                  p: { xs: 0.3, sm: 0.5 },
                  background: CARD_COLORS.Journal.bg,
                  border:
                    selectedTab === "Journal"
                      ? "2px solid #1976d2"
                      : `1px solid ${CARD_COLORS.Journal.border}`,
                  color: CARD_COLORS.Journal.text,
                  borderRadius: 1.5,
                  boxShadow: 1,
                  transition: "transform .18s ease, box-shadow .18s ease",
                  "&:hover": { transform: "translateY(-3px)", boxShadow: 3 },
                }}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: { xs: 0.3, sm: 0.5 },
                    p: { xs: "8px !important", sm: "12px !important" },
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: "0.7rem", sm: "14px" },
                      color: CARD_COLORS.Journal.text,
                      textAlign: "center",
                    }}
                  >
                    Journal Voucher
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 400,
                      fontSize: { xs: "0.65rem", sm: "14px" },
                      color: CARD_COLORS.Journal.text,
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <span>Total:</span>
                    <span>
                      ₹
                      {projectAmt.project_journal_voucher
                        ? projectAmt.project_journal_voucher?.total_amount
                        : 0}
                    </span>
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 400,
                      fontSize: { xs: "0.6rem", sm: "13px" },
                      color: CARD_COLORS.Journal.text,
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <span>Paid:</span>
                    <span>
                      ₹
                      {projectAmt.project_journal_voucher
                        ? projectAmt.project_journal_voucher?.receive_amt
                        : 0}
                    </span>
                  </Typography>
                </CardContent>
              </Card>
            </Grid> */}

            {/* 4. Sales Voucher Card */}
            {/* <Grid item xs={6} sm={6} md={4} lg={2.4}>
              <Card
                onClick={() => {
                  setSelectedTab("SalesVoucher");
                  fetchReportData("SalesVoucher");
                }}
                sx={{
                  width: "100%",
                  minHeight: { xs: 80, sm: 50 },
                  cursor: "pointer",
                  p: { xs: 0.3, sm: 0.5 },
                  background: CARD_COLORS.SalesVoucher.bg,
                  border:
                    selectedTab === "SalesVoucher"
                      ? "2px solid #1976d2"
                      : `1px solid ${CARD_COLORS.SalesVoucher.border}`,
                  color: CARD_COLORS.SalesVoucher.text,
                  borderRadius: 1.5,
                  boxShadow: 1,
                  transition: "transform .18s ease, box-shadow .18s ease",
                  "&:hover": { transform: "translateY(-3px)", boxShadow: 3 },
                }}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: { xs: 0.3, sm: 0.5 },
                    p: { xs: "8px !important", sm: "12px !important" },
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: "0.7rem", sm: "14px" },
                      color: CARD_COLORS.SalesVoucher.text,
                      textAlign: "center",
                    }}
                  >
                    Sales Voucher
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 400,
                      fontSize: { xs: "0.65rem", sm: "14px" },
                      color: CARD_COLORS.SalesVoucher.text,
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <span>Total:</span>
                    <span>
                      ₹
                      {projectAmt.project_sales_voucher
                        ? projectAmt.project_sales_voucher?.total_amount
                        : 0}
                    </span>
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 400,
                      fontSize: { xs: "0.6rem", sm: "13px" },
                      color: CARD_COLORS.SalesVoucher.text,
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <span>Rec:</span>
                    <span>
                      ₹
                      {projectAmt.project_sales_voucher
                        ? projectAmt.project_sales_voucher?.receive_amt
                        : 0}
                    </span>
                  </Typography>
                </CardContent>
              </Card>
            </Grid> */}

            {/* 5. Purchase Voucher Card */}
            <Grid item xs={6} sm={6} md={4} lg={2.4}>
              <Card
                onClick={() => {
                  setSelectedTab("PurchaseVoucher");
                  fetchReportData("PurchaseVoucher");
                }}
                sx={{
                  width: "100%",
                  minHeight: { xs: 80, sm: 50 },
                  cursor: "pointer",
                  p: { xs: 0.3, sm: 0.5 },
                  background: CARD_COLORS.PurchaseVoucher.bg,
                  border:
                    selectedTab === "PurchaseVoucher"
                      ? "2px solid #1976d2"
                      : `1px solid ${CARD_COLORS.PurchaseVoucher.border}`,
                  color: CARD_COLORS.PurchaseVoucher.text,
                  borderRadius: 1.5,
                  boxShadow: 1,
                  transition: "transform .18s ease, box-shadow .18s ease",
                  "&:hover": { transform: "translateY(-3px)", boxShadow: 3 },
                }}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: { xs: 0.3, sm: 0.5 },
                    p: { xs: "8px !important", sm: "12px !important" },
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: "0.7rem", sm: "14px" },
                      color: CARD_COLORS.PurchaseVoucher.text,
                      textAlign: "center",
                    }}
                  >
                    Purchase List
                  </Typography>
                  {/* <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 400,
                      fontSize: { xs: "0.65rem", sm: "14px" },
                      color: CARD_COLORS.PurchaseVoucher.text,
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <span>Total:</span>
                    <span>
                      ₹
                      {projectAmt.project_purchase_voucher
                        ? projectAmt.project_purchase_voucher?.total_amount
                        : 0}
                    </span>
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 400,
                      fontSize: { xs: "0.6rem", sm: "13px" },
                      color: CARD_COLORS.PurchaseVoucher.text,
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <span>Paid:</span>
                    <span>
                      ₹
                      {projectAmt.project_purchase_voucher
                        ? projectAmt.project_purchase_voucher?.receive_amt
                        : 0}
                    </span>
                  </Typography> */}
                </CardContent>
              </Card>
            </Grid>

            {/* 6. Payment Card */}
            <Grid item xs={6} sm={6} md={4} lg={2.4}>
              <Card
                onClick={() => {
                  setSelectedTab("Payment");
                  fetchReportData("Payment");
                }}
                sx={{
                  width: "100%",
                  minHeight: { xs: 80, sm: 50 },
                  cursor: "pointer",
                  p: { xs: 0.3, sm: 0.5 },
                  background: CARD_COLORS.Payment.bg,
                  border:
                    selectedTab === "Payment"
                      ? "2px solid #1976d2"
                      : `1px solid ${CARD_COLORS.Payment.border}`,
                  color: CARD_COLORS.Payment.text,
                  borderRadius: 1.5,
                  boxShadow: 1,
                  transition: "transform .18s ease, box-shadow .18s ease",
                  "&:hover": { transform: "translateY(-3px)", boxShadow: 3 },
                }}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: { xs: 0.3, sm: 0.5 },
                    p: { xs: "8px !important", sm: "12px !important" },
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: "0.7rem", sm: "14px" },
                      color: CARD_COLORS.Payment.text,
                      textAlign: "center",
                    }}
                  >
                    Purchase Orders
                  </Typography>
                  {/* <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 400,
                      fontSize: { xs: "0.65rem", sm: "14px" },
                      color: CARD_COLORS.Payment.text,
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <span>Total:</span>
                    <span>
                      ₹
                      {projectAmt.project_payment
                        ? projectAmt.project_payment?.total_amount
                        : 0}
                    </span>
                  </Typography> */}
                </CardContent>
              </Card>
            </Grid>

            {/* 7. Receipt Card */}
            {/* <Grid item xs={6} sm={6} md={4} lg={2.4}> */}
              {/* <Card
                onClick={() => {
                  setSelectedTab("Receipt");
                  fetchReportData("Receipt");
                }}
                sx={{
                  width: "100%",
                  minHeight: { xs: 80, sm: 50 },
                  cursor: "pointer",
                  p: { xs: 0.3, sm: 0.5 },
                  background: CARD_COLORS.Receipt.bg,
                  border:
                    selectedTab === "Receipt"
                      ? "2px solid #1976d2"
                      : `1px solid ${CARD_COLORS.Receipt.border}`,
                  color: CARD_COLORS.Receipt.text,
                  borderRadius: 1.5,
                  boxShadow: 1,
                  transition: "transform .18s ease, box-shadow .18s ease",
                  "&:hover": { transform: "translateY(-3px)", boxShadow: 3 },
                }}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: { xs: 0.3, sm: 0.5 },
                    p: { xs: "8px !important", sm: "12px !important" },
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: "0.7rem", sm: "14px" },
                      color: CARD_COLORS.Receipt.text,
                      textAlign: "center",
                    }}
                  >
                    Receipt
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 400,
                      fontSize: { xs: "0.65rem", sm: "14px" },
                      color: CARD_COLORS.Receipt.text,
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <span>Total:</span>
                    <span>
                      ₹
                      {projectAmt.project_receipt
                        ? projectAmt.project_receipt?.total_amount
                        : 0}
                    </span>
                  </Typography>
                </CardContent>
              </Card> */}
            {/* </Grid> */}

            {/* 8. IssueNoteInward Card */}
            <Grid item xs={6} sm={6} md={4} lg={2.4}>
              <Card
                onClick={() => {
                  setSelectedTab("IssueNoteInward");
                  fetchReportData("IssueNoteInward");
                }}
                sx={{
                  width: "100%",
                  minHeight: { xs: 80, sm: 50 },
                  cursor: "pointer",
                  p: { xs: 0.3, sm: 0.5 },
                  background: CARD_COLORS.IssueNoteInward.bg,
                  border:
                    selectedTab === "IssueNoteInward"
                      ? "2px solid #1976d2"
                      : `1px solid ${CARD_COLORS.IssueNoteInward.border}`,
                  color: CARD_COLORS.IssueNoteInward.text,
                  borderRadius: 1.5,
                  boxShadow: 1,
                  transition: "transform .18s ease, box-shadow .18s ease",
                  "&:hover": { transform: "translateY(-3px)", boxShadow: 3 },
                }}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: { xs: 0.3, sm: 0.5 },
                    p: { xs: "8px !important", sm: "12px !important" },
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: "0.7rem", sm: "14px" },
                      color: CARD_COLORS.IssueNoteInward.text,
                      textAlign: "center",
                    }}
                  >
                    Issue Note Inward
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* 9. IssueNoteOutward Card */}
            <Grid item xs={6} sm={6} md={4} lg={2.4}>
              <Card
                onClick={() => {
                  setSelectedTab("IssueNoteOutward");
                  fetchReportData("IssueNoteOutward");
                }}
                sx={{
                  width: "100%",
                  minHeight: { xs: 80, sm: 50 },
                  cursor: "pointer",
                  p: { xs: 0.3, sm: 0.5 },
                  background: CARD_COLORS.IssueNoteOutward.bg,
                  border:
                    selectedTab === "IssueNoteOutward"
                      ? "2px solid #1976d2"
                      : `1px solid ${CARD_COLORS.IssueNoteOutward.border}`,
                  color: CARD_COLORS.IssueNoteOutward.text,
                  borderRadius: 1.5,
                  boxShadow: 1,
                  transition: "transform .18s ease, box-shadow .18s ease",
                  "&:hover": { transform: "translateY(-3px)", boxShadow: 3 },
                }}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: { xs: 0.3, sm: 0.5 },
                    p: { xs: "8px !important", sm: "12px !important" },
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: "0.7rem", sm: "14px" },
                      color: CARD_COLORS.IssueNoteOutward.text,
                      textAlign: "center",
                    }}
                  >
                    Issue Note Outward
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* 10. Expense Card */}
            {/* <Grid item xs={6} sm={6} md={4} lg={2.4}>
              <Card
                onClick={() => {
                  setSelectedTab("Expense");
                  fetchReportData("expense");
                }}
                sx={{
                  width: "100%",
                  minHeight: { xs: 80, sm: 50 },
                  cursor: "pointer",
                  p: { xs: 0.3, sm: 0.5 },
                  background: CARD_COLORS.Expense.bg,
                  border:
                    selectedTab === "Expense"
                      ? "2px solid #1976d2"
                      : `1px solid ${CARD_COLORS.Expense.border}`,
                  color: CARD_COLORS.Expense.text,
                  borderRadius: 1.5,
                  boxShadow: 1,
                  transition: "transform .18s ease, box-shadow .18s ease",
                  "&:hover": { transform: "translateY(-3px)", boxShadow: 3 },
                }}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: { xs: 0.3, sm: 0.5 },
                    p: { xs: "8px !important", sm: "12px !important" },
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: "0.7rem", sm: "14px" },
                      color: CARD_COLORS.Expense.text,
                      textAlign: "center",
                    }}
                  >
                    Expense
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 400,
                      fontSize: { xs: "0.65rem", sm: "14px" },
                      color: CARD_COLORS.Expense.text,
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <span>Total:</span>
                    <span>
                      ₹
                      {projectAmt.expense
                        ? projectAmt.expense?.total_expense
                        : 0}
                    </span>
                  </Typography>
                </CardContent>
              </Card>
            </Grid> */}
          </Grid>
        </Box>
      )}

      {/* ✅ CONDITIONAL: Mobile Expandable Cards | Desktop Table */}
      {isMobile ? (
        <Box sx={{ mt: 35 }}>{renderMobileData()}</Box>
      ) : (
        <Box>{renderSection()}</Box>
      )}
    </Box>
  );
};

export default ProjectSummary;